import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { CalendarDays, ClipboardList, FolderKanban, Plus, Users } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import "./CSS/Management.css";

const API_BASE = "https://timetrakerapi.sntservices.in";
interface StaffOption { email: string; name?: string | null; }
interface ProjectOption { id: number; name: string; }
const dateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export default function AddTask() {
  const { user } = useUser();
  const adminId = user?.id;
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedEmail, setSelectedEmail] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [task, setTask] = useState("");
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!adminId) { setLoadingStaff(false); return; }
    const controller = new AbortController();
    axios.get<StaffOption[]>(`${API_BASE}/GetUser`, { params: { Adminid: adminId }, signal: controller.signal })
      .then((response) => setStaff(Array.isArray(response.data) ? response.data : []))
      .catch((err) => { if (!axios.isCancel(err)) setError("Team members could not be loaded."); })
      .finally(() => { if (!controller.signal.aborted) setLoadingStaff(false); });
    return () => controller.abort();
  }, [adminId]);

  useEffect(() => {
    setSelectedProject("");
    setProjects([]);
    if (!adminId || !selectedEmail) return;
    const controller = new AbortController();
    setLoadingProjects(true);
    axios.get<ProjectOption[]>(`${API_BASE}/getprojectdetailForUser`, { params: { Email: selectedEmail, AdminId: adminId }, signal: controller.signal })
      .then((response) => setProjects(Array.isArray(response.data) ? response.data : []))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (axios.isAxiosError(err) && err.response?.status === 404) return;
        setError("Projects for this team member could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoadingProjects(false); });
    return () => controller.abort();
  }, [adminId, selectedEmail]);

  const addTask = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedEmail || !selectedProject || !task.trim()) { setError("Choose a team member and project, then enter a task."); return; }
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/AddTask`, null, { params: { Adminid: adminId, projectid: selectedProject, email: selectedEmail, task: task.trim(), date: dateKey(selectedDate) } });
      setTask("");
      toast.success("Task added successfully");
    } catch { setError("Task could not be added. Please try again."); }
    finally { setSaving(false); }
  };

  return <main className="management-page">
    <header className="management-header"><div><span className="management-eyebrow">Workspace / Tasks</span><h1>Add task</h1><p>Create a clear assignment for one team member and project.</p></div><div className="management-header-actions"><Link className="management-button" to="/admin-dashboard/TaskList"><ClipboardList size={15} /> View task list</Link></div></header>
    {error && <div className="management-alert" role="alert">{error} <button type="button" onClick={() => setError("")}>Dismiss</button></div>}
    <form className="management-panel management-form-panel" onSubmit={addTask}><h2>Task details</h2><p>Choose the owner first to see their assigned projects.</p><div className="management-form-grid"><label className="management-field"><span><Users size={15} /> Team member</span><select className="management-select" value={selectedEmail} onChange={(event) => setSelectedEmail(event.target.value)} disabled={loadingStaff}><option value="">{loadingStaff ? "Loading team…" : "Select a team member"}</option>{staff.map((person) => <option key={person.email} value={person.email}>{person.name ? `${person.name} — ${person.email}` : person.email}</option>)}</select></label><label className="management-field"><span><FolderKanban size={15} /> Project</span><select className="management-select" value={selectedProject} onChange={(event) => setSelectedProject(event.target.value)} disabled={!selectedEmail || loadingProjects}><option value="">{loadingProjects ? "Loading projects…" : selectedEmail ? "Select a project" : "Choose a team member first"}</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></label><label className="management-field"><span><CalendarDays size={15} /> Task date</span><DatePicker selected={selectedDate} onChange={(date: Date | null) => setSelectedDate(date || new Date())} dateFormat="MMM d, yyyy" className="management-input" /></label></div><div style={{ marginTop: 22 }}><label className="management-field">Task description<textarea className="management-textarea" placeholder="Describe the work to be done…" value={task} onChange={(event) => setTask(event.target.value)} maxLength={2000} /></label><small className="management-muted">Keep the assignment specific so staff can identify it quickly.</small></div><div className="management-form-footer"><span className="management-muted">The task will appear in the selected member’s task list.</span><button className="management-button management-button-primary" disabled={saving || !selectedEmail || !selectedProject || !task.trim()}><Plus size={16} /> {saving ? "Adding task…" : "Add task"}</button></div></form>
  </main>;
}
